import type { Bulle } from '../partage/api/types';
import { ceQuElleAttendSelon } from '../moteur/conditions';
import { lignesDeLaFiche, type SorteDePastille } from './ficheDuTelephone';
import { motsDuTri } from './ficheEnMots';
import {
  ADRESSE,
  attendQuoi,
  AUX_HEURES_D_OUVERTURE,
  CONDITION,
  CONTACT,
  DELAI_D_ATTENTE,
  DIFFICULTE_COURT,
  DUREE,
  ECHEANCE,
  etoilesEnMots,
  IMPORTANCE,
  LIEN_WEB,
  OU,
  parLaBulle,
  RENDEZ_VOUS,
} from '../partage/vocabulaire';

/**
 * Ce que montre le panneau Réglages de la fiche au téléphone, sans rendu (chantier
 * `telephone-projets-fiche`, ticket 04, `design/telephone.html`, « Le panneau Réglages,
 * complet » ; revue, décision 15 ; cadrage, décisions 12 et 19).
 *
 * Trois groupes, aux couleurs de la fiche : « La tâche » (ses réglages de tri), « Quand et
 * conditions » (ce que le temps et les autres bulles imposent), « Joindre » (ce qu'on joint).
 * Dans chaque groupe, ce qui est réglé passe en tête, plein, avec ce que sa croix enlève ; puis
 * ce qui ne l'est pas, en pointillés, dans un ordre fixe. Rien ne se calcule ici sur le moteur :
 * ce qu'elle attend vient du serveur (`retenuePar`, les liens de la bulle).
 */

export type SorteDeReglage =
  | 'duree'
  | 'ou'
  | 'importance'
  | 'difficulte'
  | 'heures'
  | 'echeance'
  | 'attente'
  | 'heritee'
  | 'rdv'
  | 'delai'
  | 'condition'
  | 'adresse'
  | 'contact'
  | 'lien';

/** Un réglage du panneau : une pastille pleine (réglé) ou en pointillés (à poser). */
export interface Reglage {
  /** Unique dans le panneau : la sorte, ou la sorte et le lien d'une attente. */
  cle: string;
  sorte: SorteDeReglage;
  /** Le nom du réglage : ce que dit la pastille en pointillés, et la croix (« Enlever … »). */
  nom: string;
  regle: boolean;
  /** Le signe de la fiche devant la valeur (⚑, ●, ⧗, ⏸, ⌖, ☎, ↗), s'il y en a un. */
  signe?: string;
  /** Ce que dit la pastille : la valeur réglée, ou le nom. */
  texte: string;
  /** Sous la valeur, d'où elle vient (une attente héritée : « par « … » »). */
  mention?: string;
  /** Les champs que la croix remet à rien ; `null` : pas de croix, ou une condition à délier. */
  enlever: Partial<Bulle> | null;
  /** Une condition posée à la main : sa croix délie ce lien. */
  lienId?: string;
}

export interface GroupeDeReglages {
  nom: 'tache' | 'quand' | 'joindre';
  reglages: Reglage[];
}

/** Une condition posée à la main sur cette bulle (`api.liens(id).attend`). */
export interface ConditionPosee {
  lienId: string;
  titre: string;
}

type CeQuiSeRegle = Pick<
  Bulle,
  | 'etat'
  | 'effortMin'
  | 'canal'
  | 'prioriteChoisie'
  | 'charge'
  | 'heuresOuvrables'
  | 'dateEcheance'
  | 'debut'
  | 'dureeMin'
  | 'delaiJours'
  | 'delaiMois'
  | 'dateAction'
  | 'lieuTexte'
  | 'contactNom'
  | 'contactTel'
  | 'lien'
  | 'retenuePar'
>;

/** Les trois groupes, dans l'ordre de la maquette. */
export function groupesDesReglages(bulle: CeQuiSeRegle, conditions: ConditionPosee[]): GroupeDeReglages[] {
  const pastilles = new Map(lignesDeLaFiche(bulle).flatMap((l) => l.pastilles.map((p) => [p.sorte, p] as const)));
  const deLaFiche = (sorte: SorteDePastille) => pastilles.get(sorte) ?? null;

  // L'importance est celle qu'on a choisie : une importance reçue d'une mère n'est pas réglée ici.
  const tri = motsDuTri(bulle);
  const tache: Reglage[] = [
    reglage('duree', DUREE, tri.duree, { effortMin: null }),
    reglage('ou', OU, tri.lieu, { canal: null }),
    reglage('importance', IMPORTANCE, tri.importance, { prioriteChoisie: null }),
    reglage('difficulte', DIFFICULTE_COURT, bulle.charge != null ? `${DIFFICULTE_COURT} ${etoilesEnMots(bulle.charge)}` : null, {
      charge: null,
    }),
    reglage('heures', AUX_HEURES_D_OUVERTURE, bulle.heuresOuvrables === true ? AUX_HEURES_D_OUVERTURE : null, {
      heuresOuvrables: null,
    }),
  ];

  const echeance = deLaFiche('echeance');
  const rdv = deLaFiche('rdv');
  const delai = deLaFiche('delai');
  const attentes: Reglage[] = conditions.map((c) => ({
    cle: `attente-${c.lienId}`,
    sorte: 'attente',
    nom: CONDITION,
    regle: true,
    signe: '⏸',
    texte: attendQuoi(c.titre, 0),
    enlever: null,
    lienId: c.lienId,
  }));
  /* L'attente d'une bulle qui la contient : elle se lit ici, et se retire depuis cette bulle-là. */
  const heritees: Reglage[] = ceQuElleAttendSelon(bulle, 'reglagesDuTelephone').map((r) => ({
    cle: `heritee-${r.id}`,
    sorte: 'heritee',
    nom: CONDITION,
    regle: true,
    signe: '⏸',
    texte: attendQuoi(r.titre, 0),
    mention: parLaBulle(r.via.titre),
    enlever: null,
  }));
  const quand: Reglage[] = [
    reglage('echeance', ECHEANCE, echeance?.court ?? null, { dateEcheance: null }, echeance?.signe),
    ...attentes,
    ...heritees,
    reglage('rdv', RENDEZ_VOUS, rdv?.court ?? null, { debut: null, dureeMin: null }, rdv?.signe),
    // Le délai seul s'enlève : le jour du geste, posé en entrant en attente, reste.
    reglage('delai', DELAI_D_ATTENTE, delai?.court ?? null, { delaiJours: null, delaiMois: null }, delai?.signe),
    reglage('condition', CONDITION, null, {}),
  ];

  const lieu = deLaFiche('lieu');
  const contact = deLaFiche('contact');
  const lien = deLaFiche('lien');
  const joindre: Reglage[] = [
    reglage('adresse', ADRESSE, lieu?.court ?? null, { lieuTexte: null }, lieu?.signe),
    reglage('contact', CONTACT, contact?.court ?? null, { contactNom: null, contactTel: null }, contact?.signe),
    // Un lien qui ne s'ouvrirait pas (ni http ni https) n'a pas de pastille sur la fiche : il se
    // dit ici tel quel, en texte, pour qu'on puisse l'enlever.
    reglage('lien', LIEN_WEB, lien?.court ?? (bulle.lien || null), { lien: null }, lien?.signe),
  ];

  return [
    { nom: 'tache', reglages: enTete(tache) },
    { nom: 'quand', reglages: enTete(quand) },
    { nom: 'joindre', reglages: enTete(joindre) },
  ];
}

/** Un réglage simple : réglé s'il a une valeur, sa croix remet `enlever` à rien. */
function reglage(
  sorte: SorteDeReglage,
  nom: string,
  valeur: string | null,
  enlever: Partial<Bulle>,
  signe?: string
): Reglage {
  if (valeur === null) return { cle: sorte, sorte, nom, regle: false, texte: nom, enlever: null };
  return { cle: sorte, sorte, nom, regle: true, texte: valeur, enlever, ...(signe ? { signe } : {}) };
}

/** Ce qui est réglé d'abord, chacun gardant sa place dans son ordre. */
function enTete(reglages: Reglage[]): Reglage[] {
  return [...reglages.filter((r) => r.regle), ...reglages.filter((r) => !r.regle)];
}
